import React, { useState } from 'react';
import QuizContainer from '../Quiz/QuizContainer';
import ExperienceStep from './steps/ExperienceStep';
import StyleStep from './steps/StyleStep';
import TechnicalStep from './steps/TechnicalStep';
import PersonalityStep from './steps/PersonalityStep';
import AudienceStep from './steps/AudienceStep';
import LearningStep from './steps/LearningStep';
import DetailsStep from './steps/DetailsStep';
import BalletVariationResults from './BalletVariationResults';
import QuizEmailCapture from '../Quiz/QuizEmailCapture';
import { getVariationRecommendations, formatQuizAnswers } from '../../services/quiz/balletVariation';

const BalletVariationQuiz = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [emailCaptureCompleted, setEmailCaptureCompleted] = useState(false);
  const [userClosedEmailCapture, setUserClosedEmailCapture] = useState(false);

  const TOTAL_STEPS = 7;

  const handleStepComplete = (stepNumber, stepAnswers) => {
    const newAnswers = {
      ...answers,
      [`step${stepNumber}`]: stepAnswers
    };
    setAnswers(newAnswers);

    if (stepNumber === TOTAL_STEPS) {
      // Quiz complete, get AI recommendations
      generateRecommendations(newAnswers);
      // Scroll to top when showing results
      window.scrollTo(0, 0);
    } else {
      setCurrentStep(stepNumber + 1);
      // Scroll to top when moving to next step
      window.scrollTo(0, 0);
    }
  };

  const generateRecommendations = async (allAnswers) => {
    setIsLoading(true);
    setAiError(null);
    
    try {
      console.log('🎯 Quiz answers being sent to AI:', allAnswers);
      const formattedAnswers = formatQuizAnswers(allAnswers);
      console.log('📋 Formatted answers:', formattedAnswers);
      
      const results = await getVariationRecommendations(formattedAnswers);
      console.log('🎭 Final recommendations received:', results);
      
      setRecommendations(results);
      setShowEmailCapture(true); // Show email capture modal first
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('💥 Quiz generation failed:', error);
      setAiError(error.message);
      // Still show results even if AI failed - skip email capture for errors
      setRecommendations(null);
      setCurrentStep(TOTAL_STEPS + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top when going back to previous step
      window.scrollTo(0, 0);
    }
  };

  const resetQuiz = () => {
    setCurrentStep(1);
    setAnswers({});
    setRecommendations(null);
    setAiError(null);
    setShowEmailCapture(false);
    setEmailCaptureCompleted(false);
    setUserClosedEmailCapture(false);
    // Scroll to top when restarting quiz
    window.scrollTo(0, 0);
  };

  const handleEmailCaptureComplete = () => {
    setEmailCaptureCompleted(true);
    setShowEmailCapture(false);
    setCurrentStep(TOTAL_STEPS + 1); // Show results
  };

  const handleEmailCaptureClose = () => {
    setUserClosedEmailCapture(true);
    setShowEmailCapture(false);
    setCurrentStep(TOTAL_STEPS + 1); // Show results anyway
  };

  // Determine if we should show results
  const shouldShowResults = recommendations && (emailCaptureCompleted || userClosedEmailCapture) && currentStep === TOTAL_STEPS + 1;

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ExperienceStep 
            onComplete={(stepAnswers) => handleStepComplete(1, stepAnswers)}
            initialAnswers={answers.step1}
          />
        );
      case 2:
        return (
          <StyleStep 
            onComplete={(stepAnswers) => handleStepComplete(2, stepAnswers)}
            onBack={goBack}
            initialAnswers={answers.step2}
          />
        );
      case 3:
        return (
          <TechnicalStep 
            onComplete={(stepAnswers) => handleStepComplete(3, stepAnswers)}
            onBack={goBack}
            initialAnswers={answers.step3}
          />
        );
      case 4:
        return (
          <PersonalityStep 
            onComplete={(stepAnswers) => handleStepComplete(4, stepAnswers)}
            onBack={goBack}
            initialAnswers={answers.step4}
          />
        );
      case 5:
        return (
          <AudienceStep 
            onComplete={(stepAnswers) => handleStepComplete(5, stepAnswers)}
            onBack={goBack}
            initialAnswers={answers.step5}
          />
        );
      case 6:
        return (
          <LearningStep 
            onComplete={(stepAnswers) => handleStepComplete(6, stepAnswers)}
            onBack={goBack}
            initialAnswers={answers.step6}
          />
        );
      case 7:
        return (
          <DetailsStep 
            onComplete={(stepAnswers) => handleStepComplete(7, stepAnswers)}
            onBack={goBack}
            initialAnswers={answers.step7}
          />
        );
      case 8:
        // Only show results if email capture is completed or user closed it
        if (shouldShowResults) {
          return (
            <BalletVariationResults 
              recommendations={recommendations}
              onRestart={resetQuiz}
              aiError={aiError}
              answers={answers}
            />
          );
        }
        // If we have recommendations but email capture not completed, show nothing (modal will overlay)
        return null;
      default:
        return null;
    }
  };

  const loadingMessage = {
    title: "Creating Your Perfect Variations...",
    description: "Our AI ballet coach is analyzing your responses to find variations that match your unique style and personality.",
    note: "⏱️ This usually takes 10-15 seconds..."
  };

  return (
    <>
      <QuizContainer
        title="🩰 Which Ballet Variation is Built for You?"
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        isLoading={isLoading}
        loadingMessage={loadingMessage}
      >
        {renderCurrentStep()}
      </QuizContainer>
      
      {/* Email Capture Modal */}
      {showEmailCapture && recommendations && (
        <QuizEmailCapture 
          results={recommendations}
          quizType="ballet-variation"
          onComplete={handleEmailCaptureComplete}
          onClose={handleEmailCaptureClose}
        />
      )}
    </>
  );
};

export default BalletVariationQuiz; 